import { ApiOptions, Maybe, PagedResult } from '@csp/csp-common-model';
import {
  BioSample,
  BioSampleCode,
  BioSamplePageQuery,
  BioSampleRefBase,
  BioSampleStatusType,
  CollectionStatusValueType,
  ShippingStatusValueType,
} from '@csp/csp-common-hbs-model';
import { BioSamplingRestServiceV1 } from '@csp/dmdp-api-client';
import { toRestOptions } from '@csp/csp-fe-auth';
import { PagedResultMapper } from '@csp/csp-common-mapper';
import { Page } from '@csp/dmdp-api-common-dto';
import { BioSampleIncludeFieldsV1, BioSampleQueryTypeV1 } from '@csp/dmdp-api-hbs-dto';
import { BioSampleRefUtil } from '../util/BioSampleRefUtil';
import { BioSampleQueryMapper } from '../mapper/BioSampleQueryMapper';
import { BioSampleMapper } from '../mapper/BioSampleMapper';

const query = async (
  bioSampleQuery: BioSamplePageQuery,
  next: Maybe<string>,
  options?: ApiOptions,
): Promise<PagedResult<BioSample>> => {
  const { paging, bioSamples } = await BioSamplingRestServiceV1.queryBioSamples(
    toRestOptions(options),
    BioSampleQueryMapper.toQueryV1(bioSampleQuery),
    {
      next,
      limit: bioSampleQuery.pageSize,
      count: !!bioSampleQuery.count,
    },
  );

  return PagedResultMapper.toPagedResult(paging, BioSampleMapper.toBioSamples(bioSamples));
};

const querySampleCodesNotValidForCollection = async (sampleRefs: BioSampleRefBase[]): Promise<BioSample[]> => {
  const bioSamples = await query(
    {
      fields: [BioSampleIncludeFieldsV1.CUSTOM_STATUSES], // TODO [Mattias] Change to SAMPLE_CODE when available https://confluence.astrazeneca.com/x/v4XcH
      pageSize: Page.maxLimit,
      criteria: {
        sampleRefs,
      },
    },
    undefined,
  );

  // TODO [Mattias] Filtering is a temp solution util CONTAINER_NUMBER is a supported field in BE query API
  return bioSamples.data.filter(bioSample => BioSampleRefUtil.includesSampleCodeOrContainerRef(bioSample, sampleRefs));
};

const queryProcessableSamples = async (queryIn: BioSamplePageQuery): Promise<BioSample[]> => {
  const bioSamples = await query(
    {
      ...queryIn,
      criteria: {
        ...queryIn.criteria,
        processable: true,
      },
    },
    undefined,
  );

  return bioSamples.data;
};

const querySampleIdsValidForShipping = async (sampleIds: BioSampleCode[]): Promise<BioSample[]> => {
  const bioSamples = await query(
    {
      pageSize: Page.maxLimit,
      criteria: {
        sampleCodes: sampleIds,
        currentStatuses: [
          {
            type: BioSampleStatusType.SHIPPING,
            operatorType: BioSampleQueryTypeV1.NOT_IN,
            values: [ShippingStatusValueType.SHIPPED],
          },
          {
            type: BioSampleStatusType.COLLECTION,
            operatorType: BioSampleQueryTypeV1.IN,
            values: [CollectionStatusValueType.COLLECTED],
          },
        ],
      },
    },
    undefined,
  );

  return bioSamples.data;
};

export const BioSampleQueryService = {
  query,
  querySampleCodesNotValidForCollection,
  queryProcessableSamples,
  querySampleIdsValidForShipping,
};
